// src/lib/auth/guard-state.ts
//
// Shared decision for the route-group auth guards ((investor)/(startup)/(admin)
// _layout). The server role comes from useUserRole() — a React Query hook that
// can ERROR (network/transient) after its retries are exhausted. On error the
// role is undefined, so `isAuthorized` is false; the naive guard then redirected
// an already-authenticated user out to '/' on a blip, with a blank flash.
//
// This helper makes the error state distinct from "genuinely unauthorized", so
// the guard can show a retry screen instead of bouncing the user.

export type GuardDecision =
  | 'loading'
  | 'error'
  | 'redirect'
  | 'authorized'
  | 'step-up-required';

/**
 * The AAL dimension of the guard decision (SEC-AUTH-02c).
 *
 *   'loading'           — MFA enrollment / assurance level not yet known.
 *   'satisfied'         — either the session is AAL2, or Supabase positively
 *                         confirmed the user has no MFA factor. Both mean
 *                         "nothing left to step up to".
 *   'step-up-required'  — the user HAS a verified factor but this session is
 *                         only AAL1.
 *   'error'             — the MFA position could not be established at all.
 *                         FAILS CLOSED: routed to the retry screen, never
 *                         treated as 'satisfied'. Conflating "we could not find
 *                         out" with "confirmed not enrolled" is what let a
 *                         failed lookup switch the gate off (SEC-AUTH-02e).
 *
 * Produced by `useAALGate()` in src/lib/auth/aal.ts, which derives it from the
 * same `useRequireAAL2()` hook the sensitive-action screens use — one
 * definition of "is this session strong enough", not two that can drift.
 */
export type AALGateStatus = 'loading' | 'satisfied' | 'step-up-required' | 'error';

/**
 * Shared decision for the route-group auth guards.
 *
 * Precedence is deliberate:
 *   1. loading  — never decide on incomplete information.
 *   2. error    — a transient role-fetch failure must not bounce an
 *                 authenticated user out; show a retry instead.
 *   3. redirect — genuinely unauthorized for THIS group. Checked before AAL so
 *                 a user with no claim to the route is sent to login rather
 *                 than prompted for a step-up that still would not admit them.
 *   4. AAL      — authorized for the group, but is the session strong enough?
 *                 `aal` is optional: callers that do not gate on it keep the
 *                 previous behaviour exactly.
 */
export function resolveGuardState(input: {
  isLoading: boolean;
  isError: boolean;
  isAuthorized: boolean;
  aal?: AALGateStatus;
}): GuardDecision {
  if (input.isLoading) return 'loading';
  if (input.isError) return 'error';
  if (!input.isAuthorized) return 'redirect';

  // Protected content must not render on a session whose assurance level is
  // still unknown — rendering first and correcting later would flash real data
  // onto the screen of a session we have not yet cleared.
  if (input.aal === 'loading') return 'loading';
  if (input.aal === 'step-up-required') return 'step-up-required';
  // MFA position indeterminate → the retry screen, NOT admission. The screen
  // also offers sign-out, so this is never a trap.
  if (input.aal === 'error') return 'error';

  return 'authorized';
}
